import {HTMLAttributes, useEffect, useRef, useState} from 'react';
import {requestContext} from '../../../../common/requestContext';
import {verticalWindowScrollIntoView} from '../../../../common/shared/utils/verticalWindowScrollIntoView';
import {twMerge} from '../../../../common/twMerge';
import {useIsDarkMode} from '../../../../common/useIsDarkMode.hook';
import {List} from '../../../../design-system/atoms/List/List';
import {SingleListViewItem} from '../../../../design-system/organisms/SingleListViewItem/SingleListViewItem';
import {
  QueryFromPage,
  SingleListConnection,
} from '../../../../graphql/generated/types';
import {showImagePopup} from '../../../../imagePopup/imagePopupHandler';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {theme} from '../../../../theme';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useBuyTicketFlow} from '../../../buy/ticket/hook/useBuyTicketFlow';
import {checkWebAccessible} from '../viewer/[productId]/util/checkWebAccessible';

export interface ContentProductListConnectionContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  sortType?: string;
  connectionList: SingleListConnection[];
}

export function ContentProductListConnectionContainer({
  seriesId,
  sortType,
  connectionList,
  className,
  ...props
}: ContentProductListConnectionContainerProps) {
  const {startTicketProcess} = useBuyTicketFlow();
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;
  const lastReadProductId =
    data?.content?.serviceProperty?.pageContinue?.lastReadProductId;
  const discountRate = data?.content?.operatorProperty?.cashInfo?.discountRate;
  const pageContinue = data?.content?.serviceProperty?.pageContinue;
  const lastReadItemRef = useRef<HTMLDivElement>(null);

  const [initialized, setInitialized] = useState(false);
  const isMobile = requestContext.requestInfo.display.isMobile;
  useEffect(() => {
    if (initialized) {
      return;
    }
    // PC 에선 전체 리스트가 메인탭에 존재합니다. 그러므로 PC 에선 스크롤 초기화 부분을 진행하지 않습니다
    if (!isMobile) {
      setInitialized(true);
      return;
    }
    // sortType 이 있다는 건 최초 요청(INIT)이 아닌 사용자 액션에 의해 정렬이 변경되었다는 뜻임
    if (sortType) {
      window.scrollTo({top: 0});
    } else if (lastReadItemRef.current) {
      verticalWindowScrollIntoView({
        element: lastReadItemRef.current,
        position: 'middle',
      });
    } else {
      window.scrollTo({top: 0});
    }
    setInitialized(true);
  }, [connectionList, initialized, isMobile, sortType]);
  const isDarkMode = useIsDarkMode();
  return (
    <div
      {...props}
      {...putTiaraObjectProps({
        click: {
          layer1: '회차목록',
        },
      })}
      className={twMerge('min-h-364pxr', className)}
    >
      {connectionList && (
        <List
          gapPx={4}
          backgroundColor={
            !isMobile && !isDarkMode
              ? theme.colors['theme-bg-surface-alt']
              : theme.colors['theme-bg-surface']
          }
          direction="column"
          className={isMobile ? 'pb-60pxr' : undefined}
        >
          {connectionList.map(connection => {
            return connection.edges.map(item => (
              <div
                key={item.cursor}
                ref={
                  // 마지막 읽은 회차가 있을 경우 해당 회차로 스크롤 이동
                  item.node.single.productId === pageContinue?.productId
                    ? lastReadItemRef
                    : undefined
                }
              >
                <SingleListViewItem
                  discountRate={discountRate}
                  className="mx-11pxr mb-4pxr"
                  item={item.node}
                  isBookmarked={
                    lastReadProductId === item.node.single.productId
                  }
                  parseEpisode={true}
                  webAccessible={checkWebAccessible({
                    isMobile,
                    slideType: item.node.single.slideType,
                    isTextViewer:
                      item.node.single.operatorProperty?.isTextViewer,
                  })}
                  isMobile={isMobile}
                  onClick={() => {
                    const {productId} = item.node.single;
                    startTicketProcess({
                      seriesId,
                      targetProductId: productId,
                      nonstopWatching: false,
                      queryFrom: QueryFromPage.ContentHome,
                      pickExactly: false,
                    });
                  }}
                  onClickThumbnail={thumbnail =>
                    showImagePopup({
                      imageList: [thumbnail],
                      isMobile,
                      style: {cursor: 'pointer'},
                    })
                  }
                  {...putTiaraObjectProps({
                    ...item.node.eventLog,
                  })}
                />
              </div>
            ));
          })}
        </List>
      )}
    </div>
  );
}
